// libs
import React, { memo, useState, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';

// component
import { SHEET_TRANSITION } from 'shared/components/OverlaySheet';
import ImageGallery from './ImageGallary';
import Helpful from 'shared/components/ReviewCard/Helpful';

// icons
import CheckMarker from '@nykaa/ui-components/Icons/alert-positive';
import StarFilled from '@nykaa/ui-components/Icons/star-filled';

// styles
import {
  Details,
  User,
  Row,
  ImageList,
  ImageItem,
  Marker,
  ProductInfo,
  Tag,
  OverlaySheet,
  Article,
  Dot,
  RatingCountWrapper,
  UserImageItemWrapper,
} from './styles';

// constants
import {
  VERIFIED_USER,
  USER_IMAGE_DIMENSION,
  USER_REVIEW_IMAGE_DIMENSION,
  MARKER_SIZE,
  REVIEWED_ON,
} from './constants';
import { ROLES } from 'shared/constants';

// hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

// helpers
import { getOptimisedImageUrl } from 'shared/helpers/utils';
import { trackReviewImgClick } from './analytics';

interface IReviewCardProps {
  review: Review.IReviewData;
  productId: string;
  isLoggedInUser: boolean;
  showHelpful?: boolean;
  isMobile?: boolean;
  shouldShowTags?: boolean;
}

function ReviewCard({
  review,
  productId,
  isLoggedInUser,
  showHelpful = true,
  isMobile = true,
  shouldShowTags = false,
}: IReviewCardProps) {
  const [imageViewerIndex, setImageViewerIndex] = useState(-1);

  const theme: Theme = useTheme();
  const imageClickHandler = (index: number) => {
    trackReviewImgClick();
    setImageViewerIndex(index);
  };

  const handleImageKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      imageClickHandler(index);
    }
  };

  const OnOverLayHide = () => {
    setImageViewerIndex(-1);
  };

  const { id, description, rating, images, name, isBuyer, reviewCreationText, tags, likeCount } =
    review;

  const { manual, response } = useMemo(() => {
    const values = { color: [], size: [], manual: [], response: [] };

    if (tags) {
      Object.keys(tags).forEach((indx) => {
        const { tag, type }: Review.ITags = tags[indx];

        values[type] = [...values[type], tag];
      });
    }

    return values;
  }, [tags]);

  const reviewDetailCarouselData = useMemo(() => {
    if (!images) {
      return { reviewData: {}, imageMapForgallery: [] };
    }

    return {
      imageMapForgallery: [...images].map((item) => ({ id: String(review.id), url: item })),
      reviewData: { [String(review.id)]: review },
    };
  }, [images, review]);

  const starRatingSize = isMobile ? '16px' : '24px';

  return (
    <Article className="reviewCard" data-at="review-summary">
      <User>
        <div className="info">
          <Row>
            <div className="name">{name}</div>
            {isBuyer && (
              <Marker>
                <CheckMarker color={theme?.colors?.textSecondary} size={MARKER_SIZE} />
                <span className="verified" data-test="reviewCard-verified">
                  {VERIFIED_USER}
                </span>
                <Dot />
                {reviewCreationText && (
                  <span className="date" data-test="reviewCard-creationDate">
                    <span className="reviewedOn">
                      {REVIEWED_ON} {reviewCreationText}
                    </span>
                  </span>
                )}
              </Marker>
            )}
          </Row>
        </div>
        <RatingCountWrapper
          data-test="reviewCard-starRating"
          aria-label={`Rating: ${rating} stars`}
        >
          <div className="rating-count" data-test="rating-count" aria-hidden="true">
            {rating}
          </div>
          <span className="star-wrapper" aria-hidden="true">
            <StarFilled color={theme?.colors?.textPrimary} size={starRatingSize} />
          </span>
        </RatingCountWrapper>
      </User>

      <Details>{description}</Details>

      {isEmpty(manual) === false && (
        <ProductInfo>
          {manual.map((item, index) => (
            <>
              <Tag key={item + index} data-test="reviewCard-manual">
                {index > 0 ? <Marker>{item}</Marker> : <>{item}</>}
              </Tag>
              {index != manual.length - 1 && <Dot />}
            </>
          ))}
        </ProductInfo>
      )}

      {isEmpty(response) === false && shouldShowTags && (
        <ProductInfo>
          {response.map((item, index) => (
            <>
              {index > 0 && <Dot />}
              <Tag key={index+item} data-test="reviewCard-response">
                {item}
              </Tag>
            </>
          ))}
        </ProductInfo>
      )}

      {isEmpty(images) === false && isMobile && (
        <ImageList data-test="reviewCard-imageList">
          {images.map((item, indx) => {
            const imgSrc = getOptimisedImageUrl(item, { width: USER_IMAGE_DIMENSION * 2 });
            return (
              <UserImageItemWrapper
                key={"review-image" + indx}
                onClick={() => imageClickHandler(indx)}
                onKeyDown={(e) => handleImageKeyDown(e, indx)}
                aria-label={`View review image ${indx + 1} of ${images.length} by ${name}`}
                tabIndex={0}
                role={ROLES.BUTTON}
              >
                <ImageItem
                  height={USER_REVIEW_IMAGE_DIMENSION.height}
                  width={USER_REVIEW_IMAGE_DIMENSION.width}
                  src={imgSrc}
                  alt={`${name} thumbnail ${indx + 1}`}
                  data-test="reviewCard-imageItem"
                />
              </UserImageItemWrapper>
            );
          })}
        </ImageList>
      )}

      {imageViewerIndex !== -1 && (
        <OverlaySheet
          navKey="imageViewer"
          transitionFrom={SHEET_TRANSITION.RIGHT}
          onHide={OnOverLayHide}
        >
          <ImageGallery
            reviews={reviewDetailCarouselData.reviewData}
            images={reviewDetailCarouselData.imageMapForgallery}
            activeIndex={imageViewerIndex}
            data-test="reviewCard-ImageGallery"
          />
        </OverlaySheet>
      )}

      {showHelpful && (
        <Helpful
          id={id}
          productId={productId}
          isLoggedInUser={isLoggedInUser}
          likeCount={likeCount}
          reviewMarkedHelpful={review.isLikedByUser}
        />
      )}
    </Article>
  );
}

export default memo(ReviewCard);
